import './LandingPage.css';

import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';

import Button from '../components/common/Button';
import CircularBadge from '../components/common/CircularBadge';
import MarketingFooter
  from '../components/common/MarketingFooter/MarketingFooter';
import MarketingTopNav
  from '../components/common/MarketingTopNav/MarketingTopNav';
import PomPom from '../components/common/PomPom/PomPom';
import { colors } from '../styles/colorPalette';

const MAROON = colors.primary.main;
const ACCENT = colors.brand.accentPink;
const ACCENT_2 = colors.brand.accentTeal;

const steps = [
  { n: 1, t: 'Snap', d: 'Add athletes with a name and a photo.', c: MAROON },
  { n: 2, t: 'Score', d: 'Rate skills, take photos, record videos, and add comments all in one place.', c: ACCENT },
  { n: 3, t: 'Place', d: 'Review scores and place athletes in record time!', c: ACCENT_2 },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [, setBadgeTapCount] = useState(0);
  const [pomsSpinning, setPomsSpinning] = useState(false);

  const handleBadgeCircleClick = (): void => {
    if (pomsSpinning) {
      return;
    }

    setBadgeTapCount((previousCount) => {
      const nextCount = previousCount + 1;
      if (nextCount >= 5) {
        setPomsSpinning(true);
      }
      return nextCount;
    });
  };

  return (
    <div className={`lp-container ${pomsSpinning ? 'lp-poms-spinning' : ''}`}>
      <MarketingTopNav />

      {/* Join tryout bar */}
      <div className="lp-tryout-bar">
        <div className="lp-tryout-bar-inner">
          <p className="lp-tryout-text">At a tryout?</p>
          <Button variant="primary" compact onClick={() => navigate('/join')}>
            Join Tryout
          </Button>
        </div>
      </div>

      <div className="lp-gutter-pompoms" aria-hidden="true">
        <PomPom size="small" color="pink" className="lp-gutter-pompom lp-gutter-pompom-left-top" />
        <PomPom size="xlarge" color="teal" className="lp-gutter-pompom lp-gutter-pompom-right-top" />
        <PomPom size="medium" color="pink" className="lp-gutter-pompom lp-gutter-pompom-left-mid" />
        <PomPom size="large" color="teal" className="lp-gutter-pompom lp-gutter-pompom-right-mid" />
        <PomPom size="small" color="teal" className="lp-gutter-pompom lp-gutter-pompom-left-bottom" />
        <PomPom size="medium" color="pink" className="lp-gutter-pompom lp-gutter-pompom-right-bottom" />
        <PomPom size="xlarge" color="pink" className="lp-gutter-pompom lp-gutter-pompom-wide-left-top" />
        <PomPom size="xlarge" color="teal" className="lp-gutter-pompom lp-gutter-pompom-wide-right-top" />
        <PomPom size="large" color="teal" className="lp-gutter-pompom lp-gutter-pompom-wide-left-bottom" />
        <PomPom size="large" color="pink" className="lp-gutter-pompom lp-gutter-pompom-wide-right-bottom" />
      </div>

      <div className="lp-maxwidth-container">
        {/* Hero — big maroon block */}
        <section className="lp-hero">
          <PomPom size="large" color="pink" className="lp-hero-pompom lp-hero-pompom-top" />
          <PomPom size="medium" color="teal" className="lp-hero-pompom lp-hero-pompom-bottom" />

          <div className="lp-hero-badge">
          Customize your tryout! 
          </div>

          <h1 className="lp-hero-title">
            Run tryouts<br />
            <span className="lp-hero-accent">like a champion.</span>
          </h1>

          <p className="lp-hero-subtitle">
            Score, rank, and place athletes based on your criteria. Use photos, videos, ratings, and comments. No paper, less chaos.
          </p>

          <div className="lp-button-group">
            <button className="lp-btn-primary" onClick={() => { posthog?.capture('get_started_clicked', { source: 'landing_page' }); navigate('/createaccount'); }}>
              Get Started
            </button>
            <button className="lp-btn-ghost" onClick={() => { posthog?.capture('demo_clicked', { source: 'landing_page' }); navigate('/demo'); }}>
              Try a Demo →
            </button>
          </div>
        </section>

        {/* Spinning badge floating over the hero curve */}
        <div className="lp-badge-wrap">
          <button type="button" className="lp-badge-circle lp-badge-circle-button" onClick={handleBadgeCircleClick}>
            <CircularBadge />
          </button>
        </div>

        {/* How it works */}
        <section className="lp-steps">
          <h2 className="lp-steps-title">
            Three steps. <span className="lp-steps-accent">Teams made.</span>
          </h2>

          {steps.map((s) => (
            <div key={s.n} className="lp-step-card">
              <div className="lp-step-num" style={{ background: s.c }}>
                {s.n}
              </div>
              <div className="lp-step-body">
                <h3 className="lp-step-title">{s.t}</h3>
                <p className="lp-step-desc">{s.d}</p>
              </div>
            </div>
          ))}
        </section>

        <MarketingFooter />
      </div>
    </div>
  );
};

export default LandingPage;
